import { TextField, Typography, Box } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { formattedRfidCode } from 'utils/common/formattedRfidCode';
import { validateRfidValues } from 'utils/common/rfidValidation';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { AddRfidResponseSuccessType } from 'redux-store/slices/rfids/types';
import addRfidToRfidGroupThunk from 'redux-store/thunks/rfidGroups/addRfidToRfidGroupThunk';
import addRfidThunk from 'redux-store/thunks/rfids/addRfidThunk';
import getAllRfidsThunk from 'redux-store/thunks/rfids/getAllRfidsThunk';

import type { AddRfidModalStageProps } from '..';

const AddRfidModal: FC<AddRfidModalStageProps> = ({
  open,
  setState,
  onClose,
  groupId,
}) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const rfidCodes = useAppSelector(
    state =>
      new Set(state.rfids.items.map(rfid => formattedRfidCode(rfid.rfid))),
  );

  const [rfidNumber, setRfidNumber] = useState('');
  const [name, setName] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);

  const serialInputRef = useRef<HTMLInputElement>(null);

  const { validRfidNumber, validName, isValid } = validateRfidValues({
    rfidNumber,
    name,
    error,
    isLoading,
  });

  const handleAddRfid = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (isLoading || !validRfidNumber || !validName) return;

      const formatted = formattedRfidCode(rfidNumber);

      if (rfidCodes.has(formatted)) {
        setError(t('common:pages.rfids.rfid_already_exists'));

        return;
      }

      try {
        setLoading(true);

        const result = await dispatch(
          addRfidThunk({
            rfid: rfidNumber,
            name,
          }),
        ).unwrap();

        if (!result.success) {
          setError(i18n.exists(result.error) ? t(result.error) : result.error);

          return;
        }

        // Add to group if groupId is provided and result contains rfid
        if (typeof groupId === 'number' && 'rfid' in result) {
          await dispatch(
            addRfidToRfidGroupThunk({
              rfidGroupId: groupId,
              data: {
                rfids: [(result as AddRfidResponseSuccessType).rfid.id],
              },
            }),
          ).unwrap();
        }

        setRfidNumber('');
        setName('');
        setState({ name: '', stage: 'success' });

        await dispatch(getAllRfidsThunk({})).unwrap();

        setTimeout(() => {
          setState({ name: '', stage: 'name' });
          onClose();
        }, 1250);
      } catch (e) {
        Sentry.captureException(e, {
          extra: { rfidNumber },
          tags: { page: 'AddRfidModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      isLoading,
      validRfidNumber,
      validName,
      rfidNumber,
      name,
      rfidCodes,
      t,
      i18n,
      dispatch,
      groupId,
      setState,
      onClose,
    ],
  );

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        serialInputRef.current?.focus();
      });
    }
  }, [open]);

  const handleNavigateToInstructions = (): void => {
    setState({ name: '', stage: 'instructions' });
  };

  return (
    <Box height="100%" sx={{ overflowY: 'auto' }}>
      <form onSubmit={handleAddRfid}>
        <Box mb={3}>
          <Typography
            variant="h2"
            textAlign="center"
            mb={1}
            data-testid="add-rfid-modal"
          >
            {t('common:pages.rfids.add_new_rfid')}
          </Typography>

          <Typography variant="body1" textAlign="center" mb={1}>
            {t('common:pages.rfids.add_new_rfid_description')}
            {typeof groupId === 'number'
              ? t('common:pages.rfids.add_new_rfid_group_description')
              : null}
          </Typography>

          <Typography
            variant="body1"
            textAlign="center"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={handleNavigateToInstructions}
          >
            {t('common:pages.rfids.how_to_add_rfid')}
          </Typography>
        </Box>

        <Box mb={2}>
          <TextField
            label={t('common:pages.rfids.rfid_number')}
            name="rfid"
            value={rfidNumber}
            onChange={e => {
              if (error) setError(null);
              setRfidNumber(e.target.value);
            }}
            type="text"
            autoComplete="off"
            fullWidth
            inputProps={{
              maxLength: 30,
              'data-testid': 'rfid-number',
            }}
            variant="filled"
            disabled={isLoading}
            inputRef={serialInputRef}
          />
        </Box>

        <Box mb={4}>
          <TextField
            label={t('common:pages.rfids.rfid_name')}
            name="name"
            value={name}
            onChange={e => {
              if (error) setError(null);
              setName(e.target.value);
            }}
            inputProps={{ 'data-testid': 'rfid-name' }}
            type="text"
            autoComplete="new-name"
            fullWidth
            variant="filled"
            disabled={isLoading}
          />
        </Box>
        <Box mb={2}>
          <Button
            fullWidth
            variant="flat"
            type="submit"
            disabled={!isValid}
            size="large"
            data-testid="submit"
          >
            {t('common:add')}
          </Button>
        </Box>
        <ErrorText error={error} />
      </form>
    </Box>
  );
};

export default AddRfidModal;
